// bidderYield.js — per-bidder yield analysis (AssertiveYield-style).
//
// Aggregates the proxy's already-computed per-seat bid table (validData.tables.bids,
// built by proxy-server/prebid/inspectPrebid.js) across every captured Prebid auction
// into a bidder scorecard: bid rate, win rate, avg CPM, avg response time, timeout rate.
//
// Each Prebid row (rowType 'prebid') = one Prebid Server auction. tables.bids holds one
// row PER REQUESTED SEAT, with status 'bid' | 'won' | 'timeout' | 'nobid' and a latencyMs
// pulled from ext.responsetimemillis — so seats that were called but never responded are
// already present. That makes "bidders called" (the bid-rate denominator) exact.
//
// Scope note: only S2S bidders that appear in the Prebid Server response are visible here.
// A purely client-side bidder that never routes through PBS won't be in tables.bids.
//
// Pure function — no React, no imports. Safe from the dashboard, sign-off report, or tests.

function rowType(r) {
  return (r.type || r.requestType || '').toLowerCase();
}

// Collapse one auction's bid rows to a per-seat verdict. Batch auctions can list a seat
// multiple times (one row per imp); a seat "bid" if it bid on ANY imp, "won" if it won any.
function seatsInAuction(bidRows) {
  const bySeat = new Map();
  for (const b of bidRows || []) {
    const seat = (b.seat || '').trim();
    if (!seat) continue;
    const s = bySeat.get(seat) || { seat, bid: false, won: false, timeout: false, price: 0, latencyMs: null };
    const status = (b.status || '').toLowerCase();
    if (status === 'won') { s.won = true; s.bid = true; }
    else if (status === 'bid') s.bid = true;
    else if (status === 'timeout') s.timeout = true;
    // else 'nobid' — seat was called but returned nothing
    const price = Number(b.price);
    if (!isNaN(price) && price > s.price) s.price = price;
    if (b.latencyMs != null && !isNaN(Number(b.latencyMs))) s.latencyMs = Number(b.latencyMs);
    bySeat.set(seat, s);
  }
  return [...bySeat.values()];
}

// rows: viewer rows (live or HAR). Returns { auctions, bidders: [...], totals }.
export function computeBidderYield(rows = []) {
  const prebid = rows.filter((r) => rowType(r) === 'prebid');

  // per-seat accumulators
  const acc = new Map(); // seat -> { called, bid, won, timeout, priceSum, priceN, msSum, msN }
  const get = (seat) => {
    let a = acc.get(seat);
    if (!a) { a = { seat, called: 0, bid: 0, won: 0, timeout: 0, priceSum: 0, priceN: 0, msSum: 0, msN: 0 }; acc.set(seat, a); }
    return a;
  };

  let auctionsWithTable = 0;
  for (const r of prebid) {
    const bidRows = r.validData?.tables?.bids;
    if (!Array.isArray(bidRows) || bidRows.length === 0) continue;
    auctionsWithTable++;
    for (const s of seatsInAuction(bidRows)) {
      const a = get(s.seat);
      a.called += 1;
      if (s.bid) { a.bid += 1; if (s.price > 0) { a.priceSum += s.price; a.priceN += 1; } }
      if (s.won) a.won += 1;
      if (s.timeout && !s.bid) a.timeout += 1;
      if (s.latencyMs != null) { a.msSum += s.latencyMs; a.msN += 1; }
    }
  }

  const pct = (n, d) => (d > 0 ? Math.round((n / d) * 100) : 0);
  const bidders = [...acc.values()].map((a) => ({
    seat: a.seat,
    called: a.called,          // auctions this seat was requested in
    bids: a.bid,               // auctions it returned a bid
    wins: a.won,               // auctions it was the top Prebid bid
    timeouts: a.timeout,       // auctions it was called but timed out (no bid)
    noBids: Math.max(0, a.called - a.bid - a.timeout),
    bidRate: pct(a.bid, a.called),      // responses ÷ called
    winRate: pct(a.won, a.bid),         // top bid ÷ its bids
    avgCpm: a.priceN ? +(a.priceSum / a.priceN).toFixed(2) : null,
    avgMs: a.msN ? Math.round(a.msSum / a.msN) : null,
    timeoutRate: pct(a.timeout, a.called),
  })).sort((x, y) => y.bidRate - x.bidRate || y.bids - x.bids);

  return {
    auctions: prebid.length,
    auctionsWithTable,           // auctions that actually carried a parsed bid table
    bidders,
    totalBidders: bidders.length,
  };
}

// Compact one-liner, e.g. "8 bidders · TradeDesk 50% · PubMatic 40%".
export function bidderYieldBadge(y) {
  if (!y.bidders.length) return 'No S2S bidders observed';
  const top = y.bidders.slice(0, 2).map((b) => `${b.seat} ${b.bidRate}%`).join(' · ');
  return `${y.totalBidders} bidders · ${top}`;
}

export default computeBidderYield;
